/* ============================================================
   APP SHELL

   The sidebar is the chrome. It owns the logo, the Manage/Explore
   switcher, the sections and the account card, and it runs the
   full height of the window — there is no global header row above
   it. Below 768px it is replaced entirely by a bottom tab bar.

   Every dimension here is a value on Chakra's spacing scale
   (1 = 0.25rem = 4px), passed straight to style props rather than
   interpolated into a px string. Two exceptions are called out
   below, and both are for the same reason: they are not style
   values at all.
   ============================================================ */

/** Edge padding for the content region. */
export const LAYOUT_EDGE_PX = { base: 4, md: 6, lg: 8 } as const;

/** Labelled sidebar at >= 1180px — 240px. */
export const SIDEBAR_W = 60;

/**
 * Icon rail between 768px and 1179px — 80px. Wider than the old 64px: the nav
 * rows are 18px icons in 12px padding, and the rail also has to hold the
 * switcher's collapsed badge.
 */
export const SIDEBAR_RAIL_W = 20;

/** Sidebar padding — generous at the top, where the logo sits. */
export const SIDEBAR_PAD = { pt: 5, px: 4.5, pb: 5 } as const;
export const SIDEBAR_RAIL_PAD = { pt: 5, px: 3, pb: 4 } as const;

/**
 * Sidebar row metrics.
 *
 * `iconSize` is the odd one out: it is a `react-icons` `size` prop — an SVG
 * attribute in raw pixels, not a style prop — so the scale does not apply to it.
 */
export const SIDEBAR_ROW = {
    px: 3.5,
    py: 3,
    gap: 3,
    radius: 'lg',
    iconSize: 18,
} as const;

/** 56px — the single sticky row of breadcrumbs and actions. */
export const SUBHEADER_H = 14;

/**
 * 68px — pt + icon/label content + pb, with no extra allowance layered on top.
 *
 * A px string, not a scale step: there is no 68px stop on the spacing scale, and
 * `17` silently resolved to a raw 17px rather than to the height this names.
 */
export const TAB_BAR_H = '68px';

/**
 * The tab bar's own bottom padding — 10px, matching its `pt`, plus whatever the
 * device reserves for a home indicator.
 *
 * The inset resolves to 0 unless the document opts into `viewport-fit=cover`,
 * which this one does not, so today the term only guards the bar's labels
 * against ever ending up under a home indicator if it ever does.
 */
export const TAB_BAR_PB = 'calc(10px + env(safe-area-inset-bottom))';

/**
 * How far above the bottom of the viewport a fixed overlay has to sit to clear
 * the mobile nav. Published to the scroll region as `--mobile-nav-clearance`.
 */
export const MOBILE_NAV_CLEARANCE = `calc(${TAB_BAR_H} + env(safe-area-inset-bottom))`;

/**
 * 40px of bottom padding on the mobile scroll region.
 *
 * The tab bar is a row of the shell rather than a fixed overlay, so the scroll
 * region already ends above it and this only has to clear the 22px of FAB that
 * overhangs the bar's top edge.
 */
export const MOBILE_SCROLL_PB = 10;

/** Content region padding, and the gap between stacked cards. */
export const CONTENT_PX = { base: 3.5, md: 8 } as const;
export const CONTENT_GAP = { base: 3, md: 5 } as const;

/**
 * Breakpoint at which the sidebar auto-collapses to the rail. Chakra's `lg`
 * is 1024px, so the design's 1180px threshold needs a raw media query.
 */
export const SIDEBAR_EXPAND_QUERY = '(min-width: 1180px)';

/** Below this there is no sidebar: a bottom tab bar instead. */
export const MOBILE_QUERY = '(max-width: 767px)';

/** Persisted per user — a manual collapse beats the auto-rail. */
export const SIDEBAR_COLLAPSED_KEY = 'cohaven_sidebar_collapsed';
